import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_URL } from "../../shared/constant";
import { toast } from "react-toastify";
import axios from "axios";
import Loader from "../../shared/loader";
import { isPWA, isMobilePWA } from "../../utils/pwa";
import { isWebView } from "../../utils/webview";
import "../../styles/pwa-customers.css";

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stockData, setStockData] = useState(null);
  const [remindStockData, setRemindStockData] = useState(null);
  const [pwaMode, setPwaMode] = useState(false);
  const [mobilePWA, setMobilePWA] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isMobile, setIsMobile] = useState(false);
  const [webViewMode, setWebViewMode] = useState(false);

  const fetchStockData = () => {
    setLoading(false);

    // First API call with remind: false
    axios.post(API_URL.DASHBOARD_STOCK, { remind: false }).then(
      (res) => {
        if (res.data.status === 1) {
          setStockData(res.data.data);
        }
      },
      (error) => {
        toast.error("Failed to fetch stock data", {
          autoClose: 3000,
        });
      }
    );

    // Second API call with remind: true
    axios.post(API_URL.DASHBOARD_STOCK, { remind: true }).then(
      (res) => {
        if (res.data.status === 1) {
          setRemindStockData(res.data.data);
        }
        setLoading(false);
      },
      (error) => {
        setLoading(false);
        toast.error("Failed to fetch remind stock data", {
          autoClose: 3000,
        });
      }
    );
  };

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (pwaMode) {
        toast.success("Connection restored", { autoClose: 2000 });
        fetchStockData(); // Refresh data when back online
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      if (pwaMode) {
        toast.warning("You're offline. Some features may not work.", {
          autoClose: 3000,
        });
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [pwaMode]);

  useEffect(() => {
    fetchStockData();
    // Check PWA mode, mobile, and webview
    setPwaMode(isPWA());
    setMobilePWA(isMobilePWA());
    setIsMobile(
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    );
    setWebViewMode(isWebView());
  }, []);

  return (
    <>
      {/* PWA Header - Only show in PWA mode or mobile (not webview) */}
      {(pwaMode || (isMobile && !webViewMode)) && (
        <div className="pwa-header bg-primary text-white p-3 mb-3">
          <div className="d-flex align-items-center justify-content-between">
            <h4 className="mb-0">
              <i className="simple-icon-screen-desktop mr-2"></i>
              Dashboard ({stockData ? stockData.length : 0} products)
            </h4>
            <div className="d-flex align-items-center gap-2">
              {!isOnline && (
                <div className="pwa-status-indicator bg-warning">
                  <i className="simple-icon-wifi me-1"></i>
                  Offline
                </div>
              )}
              {mobilePWA && (
                <div className="pwa-status-indicator">
                  <i className="simple-icon-screen-smartphone me-1"></i>
                  PWA
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div
        className={` ${
          pwaMode || (isMobile && !webViewMode) ? "pwa-content" : ""
        }`}
      >
        {!(pwaMode || (isMobile && !webViewMode)) && (
          <>
            <div className="col-12">
              <div>
                <h1>Dashboard Content</h1>
                <div className="separator mb-5"></div>
                {webViewMode && (
                  <small className="text-muted">
                    <i className="simple-icon-screen-smartphone me-1"></i>
                    WebView Mode
                  </small>
                )}
              </div>
            </div>
          </>
        )}
        {loading && <Loader />}
        {!loading && (
          <>
            {pwaMode || (isMobile && !webViewMode) ? (
              // PWA Mobile Card Layout
              <div className="col-12">
                <div className="pwa-dashboard-list">
                  {pwaMode && mobilePWA && (
                    <div className="pwa-pull-hint text-center text-muted mb-3">
                      <small>
                        <i className="simple-icon-arrow-down me-1"></i>
                        Pull down to refresh
                      </small>
                    </div>
                  )}

                  {/* Low Stock Alert Cards */}
                  {remindStockData && remindStockData.length > 0 && (
                    <div className="mb-4">
                      <h5 className="mb-3 text-danger">
                        <i className="simple-icon-exclamation mr-2"></i>
                        Low Stock Alert ({remindStockData.length} products)
                      </h5>

                      {remindStockData.map((item, index) => (
                        <div key={item.id} className="pwa-dashboard-card mb-3">
                          <div className="card">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                  <h6 className="card-title mb-1 mr-1">
                                    {item.name || "N/A"}
                                  </h6>
                                 
                                </div>
                              </div>

                              <div className="pwa-dashboard-details mb-3">
                                <div className="row">
                                  <div className="col-6">
                                    <small className="text-muted d-block">
                                      Current Stock
                                    </small>
                                    
                                      {item.in_stock} - {item.unit || "N/A"}
                                    
                                  </div>
                                  <div className="col-6">
                                    <small className="text-muted d-block">
                                      Min Required
                                    </small>
                                    
                                      {item.stock_remind} - {item.unit || "N/A"}
                                    
                                  </div>
                                </div>
                                <div className="row mt-2">
                                  <div className="col-6">
                                    <small className="text-muted d-block">
                                      Price
                                    </small>
                                    <small>₹{item.price}</small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Stock Overview Cards */}
                  {stockData && stockData.length > 0 && (
                    <div className="mb-4">
                      <h5 className="mb-3 text-success">
                        <i className="simple-icon-layers mr-2"></i>
                        Stock Overview ({stockData.length} products)
                      </h5>

                      {stockData.map((item, index) => (
                        <div key={item.id} className="pwa-dashboard-card mb-3">
                          <div className="card">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                  <h6 className="card-title mb-1 mr-1">
                                    {item.name || "N/A"}
                                  </h6>
                                </div>
                              </div>

                              <div className="pwa-dashboard-details mb-3">
                                <div className="row">
                                  <div className="col-6">
                                    <small className="text-muted d-block">
                                      Current Stock
                                    </small>
                                    {item.in_stock} - {item.unit || "N/A"}
                                  </div>
                                  <div className="col-6">
                                    <small className="text-muted d-block">
                                      Min Required
                                    </small>

                                    {item.stock_remind} - {item.unit || "N/A"}
                                  </div>
                                </div>
                                <div className="row mt-2">
                                  <div className="col-6">
                                    <small className="text-muted d-block">
                                      Price
                                    </small>
                                    <small>₹{item.price}</small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Empty State */}
                  {(!stockData || stockData.length === 0) &&
                    (!remindStockData || remindStockData.length === 0) && (
                      <div className="text-center py-5">
                        <i
                          className="simple-icon-layers text-muted"
                          style={{ fontSize: "3rem" }}
                        ></i>
                        <h5 className="mt-3 text-muted">No Stock Data</h5>
                        <p className="text-muted">
                          No stock information available at the moment.
                        </p>
                      </div>
                    )}
                </div>
              </div>
            ) : (
              // WebView/Desktop Table Layout
              <div className="row justify-content-center">
                {remindStockData && remindStockData.length > 0 && (
                  <div className="col-6 mt-4">
                    <div className="card">
                      <div className="card-body">
                        <h5 className="card-title">Low Stock Alert</h5>
                        <div className="table-responsive">
                          <table className="table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Product Name</th>
                                <th>In Stock</th>
                                <th>Stock Remind</th>
                                <th>Price</th>
                              </tr>
                            </thead>
                            <tbody>
                              {remindStockData.map((item, index) => (
                                <tr key={item.id}>
                                  <td>{index + 1}</td>
                                  <td>{item.name || "N/A"}</td>
                                  <td>{item.in_stock} - {item.unit || "N/A"}</td>
                                  <td>
                                    {item.stock_remind} - {item.unit || "N/A"}
                                  </td>
                                  <td>₹{item.price}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {stockData && stockData.length > 0 && (
                  <div className="col-6 mt-4">
                    <div className="card">
                      <div className="card-body">
                        <h5 className="card-title">Stock Overview</h5>
                        <div className="table-responsive">
                          <table className="table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Product Name</th>
                                <th>In Stock</th>
                                <th>Stock Remind</th>
                                <th>Price</th>
                              </tr>
                            </thead>
                            <tbody>
                              {stockData.map((item, index) => (
                                <tr key={item.id}>
                                  <td>{index + 1}</td>
                                  <td>{item.name || "N/A"}</td>
                                  <td>{item.in_stock} - {item.unit || "N/A"}</td>
                                  <td>
                                    {item.stock_remind} - {item.unit || "N/A"}
                                  </td>
                                  <td>₹{item.price}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Dashboard;
